import { useTranslation } from 'react-i18next';

import { stringIncludesInsensitiveAccents } from '../../../utils/normalizeForSearch';

// Components
import { ColumnManagement, ColumnManagementSection, ColumnManagementSectionList } from '../ColumnManagement/ColumnManagement';
import {
  CustomVueSectionTitle,
  CustomVueSectionContent,
} from '../../atoms/CustomVueSection/CustomVueSection';
import ListItem from '../../molecules/ListItem/ListItem';
import IconButton from '../../atoms/IconButton/IconButton';
import Button from '../../atoms/Button/Button';
import ColumnsVisibility from '../../molecules/ColumnsVisibility/ColumnsVisibility';
import GroupList from '../../molecules/GroupList/GroupList';
import SearchField from '../../molecules/SearchField/SearchField';

// Styles
import styles from './ResourcesManagementPopover.module.css';

/**
 * Resources Management Popover component
 * @param {object} props
 * @param {Array<CalendarResource>} props.allResources - All available resources
 * @param {(resourceId: string) => boolean} props.isResourceVisible - Function to check if resource is visible
 * @param {(resourceId: string) => void} props.onToggleResource - Function to toggle resource visibility
 * @param {Array<{lib: string, options: Array<CalendarResource>}>} props.resourcesGroups - Resources grouped in the format expected by ColumnsVisibility
 * @param {string | null} props.resourceGroupField - The field name used for grouping
 * @param {(groupValue: string) => 'all-visible' | 'all-hidden' | 'mixed' | null} props.getGroupVisibilityState - Function to get group visibility state
 * @param {(groupValue: string, groupField: string) => void} [props.onToggleGroup] - Function to toggle group visibility
 * @param {string} props.searchResources - Search value for filtering resources
 * @param {(search: string) => void} props.setSearchResources - Set search value
 * @param {() => void} props.onCancel - Cancel handler
 * @param {() => void} props.onApply - Apply handler
 * @returns {React.ReactElement}
 */
function ResourcesManagementPopover({
  allResources,
  isResourceVisible,
  onToggleResource,
  resourcesGroups,
  resourceGroupField,
  getGroupVisibilityState,
  onToggleGroup,
  searchResources,
  setSearchResources,
  onCancel,
  onApply,
}) {
  const { t } = useTranslation();

  // Filter resources by search
  const filterResourcesBySearch = (/** @type {Array<CalendarResource>} */ resources) => {
    if (!searchResources) return resources;
    return resources.filter((resource) => {
      const titleMatch = stringIncludesInsensitiveAccents(resource.title, searchResources);
      // Also search in group field if available
      if (resourceGroupField) {
        const groupMatch = stringIncludesInsensitiveAccents(resource[resourceGroupField], searchResources);
        return titleMatch || groupMatch;
      }
      return titleMatch;
    });
  };

  const visibleResources = allResources.filter((resource) => isResourceVisible(resource.id));
  const hiddenResources = allResources.filter((resource) => !isResourceVisible(resource.id));

  const visibleResourcesFiltered = filterResourcesBySearch(visibleResources);
  const hiddenResourcesFiltered = filterResourcesBySearch(hiddenResources);

  const renderResourceList = (/** @type {Array<CalendarResource>} */ resources, /** @type {boolean} */ isVisible) => {
    if (resources.length === 0) {
      return (
        <p className={styles['empty-message']}>
          {isVisible ? 'Aucune ressource visible' : 'Aucune ressource cachée'}
        </p>
      );
    }

    if (!resourceGroupField) {
      // No grouping, display flat list
      return resources.map((resource) => (
        <ListItem
          key={resource.id}
          id={resource.id}
          label={resource.title}
          isDisabled
        >
          <IconButton
            icon={isVisible ? 'eye-slash' : 'eye'}
            color="transparent"
            size="medium"
            onClick={() => onToggleResource(resource.id)}
          />
        </ListItem>
      ));
    }

    // Grouped display using ColumnsVisibility and GroupList (like FilterColumnManagement)
    const groupsToShow = resourcesGroups.map((group) => {
      const groupVisibleResources = group.options.filter((r) => isResourceVisible(r.id));
      const groupHiddenResources = group.options.filter((r) => !isResourceVisible(r.id));
      const resourcesToShow = isVisible ? groupVisibleResources : groupHiddenResources;
      // Filter by search
      const filteredResources = filterResourcesBySearch(resourcesToShow);

      return {
        lib: group.lib,
        options: filteredResources,
      };
    }).filter((group) => group.options.length > 0);

    return (
      <ColumnsVisibility
        options={groupsToShow}
        messageNoOptions={isVisible ? 'Aucune ressource visible' : 'Aucune ressource cachée'}
      >
        {(label, options) => {
          const groupValue = label === 'Sans groupe' ? '' : label;
          const groupState = getGroupVisibilityState(groupValue);

          let groupTitle = 'Basculer le groupe';
          if (groupState === 'all-visible') {
            groupTitle = 'Masquer tout le groupe';
          } else if (groupState === 'all-hidden') {
            groupTitle = 'Afficher tout le groupe';
          }

          return (
            <GroupList
              key={label}
              label={label}
              items={options}
              action={onToggleGroup && resourceGroupField ? (
                <IconButton
                  icon={groupState === 'all-visible' ? 'eye-slash' : 'eye'}
                  color="transparent"
                  size="small"
                  onClick={() => onToggleGroup(groupValue, resourceGroupField)}
                  title={groupTitle}
                />
              ) : null}
            >
              {(resource) => (
                <ListItem
                  key={resource.id}
                  id={resource.id}
                  label={resource.title}
                  isDisabled
                >
                  <IconButton
                    icon={isVisible ? 'eye-slash' : 'eye'}
                    color="transparent"
                    size="medium"
                    onClick={() => onToggleResource(resource.id)}
                  />
                </ListItem>
              )}
            </GroupList>
          );
        }}
      </ColumnsVisibility>
    );
  };

  return (
    <ColumnManagement
      leftChildren={(
        <ColumnManagementSection>
          <CustomVueSectionTitle>
            Ressources visibles
          </CustomVueSectionTitle>
          <ColumnManagementSectionList>
            {renderResourceList(visibleResourcesFiltered, true)}
          </ColumnManagementSectionList>
        </ColumnManagementSection>
      )}
      rightChildren={(
        <ColumnManagementSection>
          <CustomVueSectionTitle>
            Ressources cachées
          </CustomVueSectionTitle>
          <CustomVueSectionContent>
            <SearchField
              id="resources-search"
              placeholder={t('table.filters.columnsVisibilitySearch')}
              onFocus={() => {}}
              disabled={false}
              value={searchResources}
              onChange={(e) => setSearchResources(e.target.value)}
            />
          </CustomVueSectionContent>
          <ColumnManagementSectionList>
            {renderResourceList(hiddenResourcesFiltered, false)}
          </ColumnManagementSectionList>
        </ColumnManagementSection>
      )}
    >
      <>
        <Button
          color="primary100"
          size="small"
          onClick={onCancel}
        >
          Annuler
        </Button>

        <Button
          size="small"
          onClick={onApply}
        >
          Appliquer
        </Button>
      </>
    </ColumnManagement>
  );
}

export default ResourcesManagementPopover;
